import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  type ColumnField,
  columnsUrlParamName,
  getDefaultColumns,
  getHiddenColumns,
  moveColumn,
  saveCachedColumns,
  validateColumns,
} from "./columnUtils";

interface UseDataTableColumnsOptions {
  tableId?: string;
  enableColumnSelection?: boolean;
  allColumns: ColumnField[];
}

interface UseDataTableColumnsReturn {
  visibleColumns: string[];
  defaultColumns: string[];
  hiddenColumns: string[];
  handleColumnToggle: (fieldName: string) => void;
  handleColumnMove: (fromIndex: number, toIndex: number) => void;
  setDefaultColumns: (
    newColumns: string[] | ((prev: string[]) => string[]),
  ) => void;
  updateVisibleColumns: (newVisibleColumns: string[]) => void;
  isCurrentSelectionDefault: boolean;
}

/**
 * Comprehensive custom hook to handle DataTable column management.
 * Handles visible columns, default columns, and column toggling.
 * URL initialization should be handled by useDataTableUrlInit in parent components.
 */
export const useDataTableColumns = ({
  tableId,
  enableColumnSelection = false,
  allColumns,
}: UseDataTableColumnsOptions): UseDataTableColumnsReturn => {
  const [urlParams, setUrlParams] = useSearchParams();

  const [defaultColumns, _setDefaultColumns] = useState<string[]>(() =>
    getDefaultColumns(tableId, allColumns),
  );

  const setDefaultColumns = useCallback(
    (newColumns: string[] | ((prev: string[]) => string[])) => {
      _setDefaultColumns((prev) => {
        const next =
          typeof newColumns === "function" ? newColumns(prev) : newColumns;
        if (!tableId) return prev;

        const success = saveCachedColumns(tableId, next);
        return success ? next : prev;
      });
    },
    [tableId],
  );

  const visibleColumns = useMemo(() => {
    if (!enableColumnSelection) return allColumns.map((col) => col.fieldName);

    // Use the columns from the URL if set, otherwise use the default columns
    const urlColumns = urlParams.get(columnsUrlParamName);
    if (!urlColumns) {
      return defaultColumns;
    }

    try {
      const columnNames = urlColumns.split(",").filter(Boolean);
      const validColumns = validateColumns(columnNames, allColumns);
      return validColumns.length > 0 ? validColumns : defaultColumns;
    } catch {
      return defaultColumns;
    }
  }, [urlParams, allColumns, enableColumnSelection, defaultColumns]);

  const updateVisibleColumns = useCallback(
    (newVisibleColumns: string[]) => {
      const newUrlParams = new URLSearchParams(urlParams);
      newUrlParams.set(columnsUrlParamName, newVisibleColumns.join(","));
      setUrlParams(newUrlParams);
    },
    [setUrlParams, urlParams],
  );

  const handleColumnToggle = useCallback(
    (fieldName: string) => {
      const newVisibleColumns = visibleColumns.includes(fieldName)
        ? visibleColumns.filter((name) => name !== fieldName)
        : [...visibleColumns, fieldName];

      // Ensure at least one column is always visible
      if (newVisibleColumns.length > 0) {
        updateVisibleColumns(newVisibleColumns);
      }
    },
    [visibleColumns, updateVisibleColumns],
  );

  const handleColumnMove = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      const newVisibleColumns = moveColumn(visibleColumns, fromIndex, toIndex);
      updateVisibleColumns(newVisibleColumns);
    },
    [visibleColumns, updateVisibleColumns],
  );

  const isCurrentSelectionDefault = useMemo(() => {
    if (!tableId) return false;
    if (!defaultColumns) return false;

    return (
      defaultColumns.length === visibleColumns.length &&
      defaultColumns.every((col, index) => visibleColumns[index] === col)
    );
  }, [tableId, visibleColumns, defaultColumns]);

  const hiddenColumns = useMemo(
    () => getHiddenColumns(visibleColumns, allColumns),
    [visibleColumns, allColumns],
  );

  return {
    visibleColumns,
    hiddenColumns,
    defaultColumns,
    handleColumnToggle,
    handleColumnMove,
    setDefaultColumns,
    updateVisibleColumns,
    isCurrentSelectionDefault,
  };
};
